import { HttpStatusCode, isAxiosError, type AxiosInstance } from "axios";
import { SendToTrackingService } from "@/api/handlers/errors";
import {
  type MapPlotRequestDetailFile,
  type MapPlotRequestDetailFileGroup,
  type MapPlotRequestDetailFileListResponse,
  type MapPlotRequestDetailFileDownloadResponse,
} from "@/features/map/types";

const TRACKING_ERROR_STATUS_THRESHOLD = HttpStatusCode.InternalServerError;
export const MAP_PLOT_REQUEST_DETAIL_FILE_GROUPS: MapPlotRequestDetailFileGroup[] =
  ["document", "image", "spreadsheet", "other"];

export const MAP_PLOT_REQUEST_DETAIL_FILE_SORT_BY_VALUES = [
  "uploaded_at",
  "name",
  "file_type",
] as const;
export const MAP_PLOT_REQUEST_DETAIL_FILE_SORT_ORDER_VALUES = [
  "asc",
  "desc",
] as const;

type MapPlotRequestDetailFileSortByValues =
  typeof MAP_PLOT_REQUEST_DETAIL_FILE_SORT_BY_VALUES;
type MapPlotRequestDetailFileSortOrderValues =
  typeof MAP_PLOT_REQUEST_DETAIL_FILE_SORT_ORDER_VALUES;

export type MapPlotRequestDetailFileSortBy =
  MapPlotRequestDetailFileSortByValues[number];
export type MapPlotRequestDetailFileSortOrder =
  MapPlotRequestDetailFileSortOrderValues[number];

export const DEFAULT_MAP_PLOT_REQUEST_DETAIL_FILE_SORT_BY: MapPlotRequestDetailFileSortBy =
  "uploaded_at";
export const DEFAULT_MAP_PLOT_REQUEST_DETAIL_FILE_SORT_ORDER: MapPlotRequestDetailFileSortOrder =
  "desc";

const isMapPlotRequestDetailFileGroup = (
  value: unknown
): value is MapPlotRequestDetailFileGroup => {
  return (
    typeof value === "string" &&
    MAP_PLOT_REQUEST_DETAIL_FILE_GROUPS.includes(
      value as MapPlotRequestDetailFileGroup
    )
  );
};

const trackServerError = (error: unknown): void => {
  if (
    isAxiosError(error) &&
    (error.response?.status ?? TRACKING_ERROR_STATUS_THRESHOLD) >=
      TRACKING_ERROR_STATUS_THRESHOLD
  ) {
    SendToTrackingService(error);
  }
};

const toMapPlotRequestDetailFile = (
  value: unknown,
  errorMessage: string
): MapPlotRequestDetailFile => {
  if (typeof value !== "object" || value === null) {
    throw new Error(errorMessage);
  }

  const item = value as Record<string, unknown>;
  if (
    typeof item.id !== "number" ||
    typeof item.mapPlotRequestDetailId !== "number" ||
    typeof item.displayName !== "string" ||
    typeof item.originalFilename !== "string" ||
    typeof item.contentType !== "string" ||
    typeof item.fileSize !== "number" ||
    typeof item.fileExtension !== "string" ||
    !isMapPlotRequestDetailFileGroup(item.fileGroup) ||
    typeof item.createdAt !== "string" ||
    typeof item.updatedAt !== "string"
  ) {
    throw new Error(errorMessage);
  }

  return {
    id: item.id,
    mapPlotRequestDetailId: item.mapPlotRequestDetailId,
    displayName: item.displayName,
    originalFilename: item.originalFilename,
    contentType: item.contentType,
    fileSize: item.fileSize,
    fileExtension: item.fileExtension,
    fileGroup: item.fileGroup,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  };
};

const normalizeMapPlotRequestDetailFileListResponse = (
  value: unknown
): MapPlotRequestDetailFileListResponse => {
  if (typeof value !== "object" || value === null) {
    throw new Error("資料一覧の取得に失敗しました");
  }

  const record = value as Record<string, unknown>;
  if (!Array.isArray(record.list) || typeof record.count !== "number") {
    throw new Error("資料一覧の取得に失敗しました");
  }

  const normalizedList = record.list.map((item) =>
    toMapPlotRequestDetailFile(item, "資料一覧の取得に失敗しました")
  );

  return {
    count: record.count,
    list: normalizedList,
  };
};

/**
 * ダウンロードURL取得APIのレスポンスを正規化する。
 * 必須の `downloadUrl` / `contentDisposition` が不正な場合は例外を投げる。
 */
const normalizeMapPlotRequestDetailFileDownloadResponse = (
  value: unknown
): MapPlotRequestDetailFileDownloadResponse => {
  if (typeof value !== "object" || value === null) {
    throw new Error("ダウンロードURLの取得に失敗しました");
  }

  const record = value as Record<string, unknown>;
  if (
    typeof record.downloadUrl !== "string" ||
    typeof record.contentDisposition !== "string"
  ) {
    throw new Error("ダウンロードURLの取得に失敗しました");
  }

  return {
    downloadUrl: record.downloadUrl,
    contentDisposition: record.contentDisposition,
  };
};

export const listMapPlotRequestDetailFiles = async (
  apiClient: AxiosInstance,
  detailId: number,
  page: number,
  size: number,
  search = "",
  sortBy: MapPlotRequestDetailFileSortBy = DEFAULT_MAP_PLOT_REQUEST_DETAIL_FILE_SORT_BY,
  sortOrder: MapPlotRequestDetailFileSortOrder = DEFAULT_MAP_PLOT_REQUEST_DETAIL_FILE_SORT_ORDER
): Promise<MapPlotRequestDetailFileListResponse> => {
  try {
    const response = await apiClient.get(
      `/map/plot-request-details/${detailId}/files`,
      {
        params: {
          page,
          size,
          search,
          sortBy,
          sortOrder,
        },
      }
    );

    if (response.status !== HttpStatusCode.Ok) {
      throw new Error("資料一覧の取得に失敗しました");
    }

    return normalizeMapPlotRequestDetailFileListResponse(response.data);
  } catch (error) {
    trackServerError(error);
    throw error;
  }
};

export const updateMapPlotRequestDetailFileDisplayName = async (
  apiClient: AxiosInstance,
  detailId: number,
  fileId: number,
  displayName: string
): Promise<MapPlotRequestDetailFile> => {
  try {
    const response = await apiClient.patch(
      `/map/plot-request-details/${detailId}/files/${fileId}`,
      {
        displayName,
      }
    );

    if (response.status !== HttpStatusCode.Ok) {
      throw new Error("資料表示名の更新に失敗しました");
    }

    return toMapPlotRequestDetailFile(
      response.data,
      "資料表示名の更新に失敗しました"
    );
  } catch (error) {
    trackServerError(error);
    throw error;
  }
};

export const uploadMapPlotRequestDetailFile = async (
  apiClient: AxiosInstance,
  detailId: number,
  file: File
): Promise<MapPlotRequestDetailFile> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await apiClient.post(
      `/map/plot-request-details/${detailId}/files`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    if (
      response.status !== HttpStatusCode.Created &&
      response.status !== HttpStatusCode.Ok
    ) {
      throw new Error("資料のアップロードに失敗しました");
    }

    const normalized = toMapPlotRequestDetailFile(
      response.data,
      "資料のアップロードに失敗しました"
    );

    return normalized;
  } catch (error) {
    trackServerError(error);
    throw error;
  }
};

export const getMapPlotRequestDetailFileDownload = async (
  apiClient: AxiosInstance,
  detailId: number,
  fileId: number,
  preview = false
): Promise<MapPlotRequestDetailFileDownloadResponse> => {
  try {
    const response = await apiClient.get(
      `/map/plot-request-details/${detailId}/files/${fileId}/download`,
      {
        params: {
          preview,
        },
      }
    );

    if (response.status !== HttpStatusCode.Ok) {
      throw new Error("ダウンロードURLの取得に失敗しました");
    }

    return normalizeMapPlotRequestDetailFileDownloadResponse(response.data);
  } catch (error) {
    trackServerError(error);
    throw error;
  }
};

export const deleteMapPlotRequestDetailFile = async (
  apiClient: AxiosInstance,
  detailId: number,
  fileId: number
): Promise<void> => {
  try {
    const response = await apiClient.delete(
      `/map/plot-request-details/${detailId}/files/${fileId}`
    );

    if (
      response.status !== HttpStatusCode.Ok &&
      response.status !== HttpStatusCode.NoContent
    ) {
      throw new Error("資料の削除に失敗しました");
    }
  } catch (error) {
    trackServerError(error);
    throw error;
  }
};
